import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";

export const FORGOT = {
    emailInput: "forgot-email-input",
    submitBtn: "forgot-submit-btn",
    sentMessage: "forgot-sent-message",
};

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [sent, setSent] = useState(false);
    const [busy, setBusy] = useState(false);

    async function submit(e) {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/auth/forgot-password", { email });
            setSent(true);
            toast.success("Check your email");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-md rounded-3xl border-stone-200">
                <CardContent className="p-8">
                    <Link to="/auth?mode=login" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-800 mb-6">
                        <ArrowLeft className="w-4 h-4" /> Back to sign in
                    </Link>
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center mb-4">
                        <Mail className="w-5 h-5 text-emerald-700" strokeWidth={1.75} />
                    </div>
                    <h1 className="font-outfit text-2xl font-semibold text-stone-900 mb-1">Forgot your password?</h1>
                    <p className="text-sm text-stone-500 font-dmsans mb-6">
                        Enter the email on your account. We&rsquo;ll send a reset link that expires in 60 minutes.
                    </p>

                    {sent ? (
                        <div data-testid={FORGOT.sentMessage} className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl text-sm text-emerald-800 font-dmsans">
                            If <strong>{email}</strong> matches an account, a reset link is on its way.
                            {" "}(Emails run in mocked mode until you set <code>RESEND_API_KEY</code> — check the backend log for the link.)
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-4">
                            <div>
                                <Label>Email</Label>
                                <Input
                                    data-testid={FORGOT.emailInput}
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="mt-1.5 h-12 rounded-xl"
                                    placeholder="you@work.com"
                                />
                            </div>
                            <Button
                                data-testid={FORGOT.submitBtn}
                                type="submit"
                                disabled={busy}
                                className="w-full h-12 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white"
                            >
                                {busy ? "Sending…" : "Send reset link"}
                            </Button>
                        </form>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
